<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Fournisseurs ({{ totalRows }})</h1>
          <small class="text-muted">
            Gestion des fournisseurs et accès rapide à leur historique d’achats.
          </small>
        </div>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['suppliers', 'Supplier', 'product_suppliers', 'ProductSupplier']" label="Fournisseurs" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

          <button class="btn btn-primary btn-sm btn-icon-label" type="button" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouveau fournisseur</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- Filtres -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
      <div class="row g-2 align-items-end">
        <div class="col-md-3 d-flex align-items-center">
          <div class="form-check d-flex align-items-center gap-2 ps-0">
            <PrimevueCheckbox v-model="filters.only_active" binary inputId="onlyActive" @change="fetchSuppliers" />
            <label class="form-check-label small" for="onlyActive">
              Afficher uniquement les fournisseurs actifs
            </label>
          </div>
        </div>
        <div class="col-12 module-filter-actions mt-2">
          <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
            <i class="fa fa-rotate-left"></i>
            <span class="btn-label ms-1">Réinitialiser</span>
          </button>
          <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchSuppliers">
            <i class="fa fa-search"></i>
            <span class="btn-label ms-1">Filtrer</span>
          </button>
        </div>
      </div>
        </div>
      </div>

      <BootstrapDataTable
        :rows="filteredSuppliers"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkUpdateSuppliers"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectSupplier"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :active-filter-count="activeFilterCount"
        state-key="suppliers.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher fournisseur, contact, téléphone..."
        detail-route-name="SupplierShow"
        empty-text="Aucun fournisseur trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @refresh="fetchSuppliers"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button
            type="button"
            class="btn btn-sm btn-outline-success"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkSetSupplierStatus('activate')"
          >
            <i class="fa fa-check me-1"></i>Activer {{ selectedCount }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkSetSupplierStatus('deactivate')"
          >
            <i class="fa fa-ban me-1"></i>Désactiver {{ selectedCount }}
          </button>
        </template>

        <template #cell-purchases_count="{ row }">
          <span class="badge bg-light text-dark">
            {{ row.purchases_count || 0 }}
          </span>
        </template>

        <template #cell-is_active="{ row }">
          <span class="badge" :class="row.is_active ? 'bg-success' : 'bg-secondary'">
            {{ row.is_active ? 'Oui' : 'Non' }}
          </span>
        </template>
      </BootstrapDataTable>

      <!-- Dialog création / édition -->
      <PrimevueDialog v-model:visible="formDialogVisible" :modal="true" :style="{ width: '600px' }" :closable="false">
        <template #header>
          <h5 class="modal-title mb-0">
            {{ formDialogMode === 'create' ? 'Nouveau fournisseur' : 'Modifier le fournisseur' }}
          </h5>
        </template>

        <SupplierForm :mode="formDialogMode" :supplier="selectedSupplier" :submitting="isSavingForm" @save="handleSave"
          @cancel="closeFormDialog" />
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import SupplierForm from './SupplierForm.vue';
import {
  countActiveFilters,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';

const FILTER_MODULE = 'suppliers';
const DEFAULT_FILTERS = {
  search: '',
  only_active: true,
};

export default {
  name: 'SuppliersView',
  mixins: [bulkActionViewMixin],
  components: {
    SupplierForm,
  },
  data() {
    return {
      suppliers: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,
      table: {
        sortBy: 'name',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'phone', label: 'Téléphone', sortable: true },
        { key: 'city', label: 'Ville', sortable: true },
        { key: 'purchases_count', label: 'Achats', sortable: true, class: 'text-center' },
        { key: 'is_active', label: 'Actif', sortable: true, class: 'text-center' },
      ],

      formDialogVisible: false,
      formDialogMode: 'create', // 'create' | 'edit'
      selectedSupplier: null,
      isSavingForm: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'] || null;
    },
    canBulkUpdateSuppliers() {
      return hasPermission(this.currentUser, 'suppliers.update');
    },
    totalRows() {
      return this.filteredSuppliers.length;
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    filteredSuppliers() {
      const term = this.filters.search.trim().toLowerCase();
      if (!term) return this.suppliers;

      return this.suppliers.filter((s) => {
        return (
          (s.name && s.name.toLowerCase().includes(term)) ||
          (s.contact_name && s.contact_name.toLowerCase().includes(term)) ||
          (s.phone && s.phone.toLowerCase().includes(term)) ||
          (s.email && s.email.toLowerCase().includes(term))
        );
      });
    },
  },
  mounted() {
    this.restoreFilters();
    this.fetchSuppliers();
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    canSelectSupplier(row) {
      return row?.type !== 'system' && !row?.system_key;
    },

    async bulkSetSupplierStatus(action) {
      const isActivation = action === 'activate';

      await this.performBulkAction({
        url: `/suppliers/bulk/${action}`,
        method: 'patch',
        itemLabel: 'fournisseur',
        confirmation: (count) => ({
          header: isActivation ? 'Activer les fournisseurs' : 'Désactiver les fournisseurs',
          message: `${isActivation ? 'Activer' : 'Désactiver'} les ${count} fournisseurs sélectionnés ?`,
          acceptLabel: `${isActivation ? 'Activer' : 'Désactiver'} ${count}`,
          acceptClass: isActivation ? 'p-button-success' : 'p-button-danger',
        }),
        refresh: () => this.fetchSuppliers(),
      });
    },

    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.fetchSuppliers();
    },

    handleTableSearch(search) {
      this.filters.search = search;
    },

    async fetchSuppliers() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const res = await this.$api.get('/suppliers', {
          params: { only_active: this.filters.only_active ? 'true' : 'false' },
        });
        this.suppliers = res.data.data || res.data || [];
      } catch (e) {
        console.error('Erreur chargement fournisseurs', e);
        this.errorMessage = 'Impossible de charger les fournisseurs.';
      } finally {
        this.isLoading = false;
      }
    },

    openCreateDialog() {
      this.formDialogMode = 'create';
      this.selectedSupplier = null;
      this.formDialogVisible = true;
    },

    openEditDialog(row) {
      this.formDialogMode = 'edit';
      this.selectedSupplier = row;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedSupplier = null;
    },

    async handleSave(payload) {
      if (this.isSavingForm) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingForm = true;

      try {
        if (this.formDialogMode === 'create') {
          await this.$api.post('/suppliers', payload);
          this.successMessage = 'Fournisseur créé avec succès.';
        } else {
          const id = payload.id || (this.selectedSupplier && this.selectedSupplier.id);
          await this.$api.put(`/suppliers/${id}`, payload);
          this.successMessage = 'Fournisseur mis à jour avec succès.';
        }

        this.formDialogVisible = false;
        await this.fetchSuppliers();
      } catch (error) {
        console.error('Erreur enregistrement fournisseur', error);
        const msg =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(' ')
            : 'Une erreur est survenue lors de la sauvegarde du fournisseur.';
        this.errorMessage = msg;
      } finally {
        this.isSavingForm = false;
      }
    },

    async deleteSupplier(row) {
      if ((row.purchases_count || 0) > 0) {
        alert(
          "Impossible de supprimer ce fournisseur car des achats lui sont associés. " +
          'Vous pouvez le désactiver à la place.'
        );
        return;
      }

      const ok = await this.$confirmAction(`Supprimer le fournisseur "${row.name}" ?`);
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/suppliers/${row.id}`);
        this.successMessage = 'Fournisseur supprimé.';
        await this.fetchSuppliers();
      } catch (error) {
        console.error('Erreur suppression fournisseur', error);
        const msg =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(' ')
            : "Une erreur est survenue lors de la suppression du fournisseur.";
        this.errorMessage = msg;
      }
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
    },
  },
};
</script>
